import { useState } from "react";
import { useFeature } from "@optimizely/react-sdk";

import { getClimateAlertNames, getWarningNames } from "../../utils/alertUtils";
import { renderSideLine, renderTags } from "../../utils/utils";
import DropDownMenu from "../common/DropDownMenu";
import {
  ESubscriptionStatus,
  IHomeSubscription,
  IUnitDetailsDataHome,
} from "../../interfaces/interfaces";
import { ReactComponent as Key } from "../../img/key.svg";
import { ReactComponent as More } from "../../img/header/More.svg";
import { renderUnitDetailsElementAmount } from "../unitDetails/common/CommonUnitDetailsComponents";

export interface HomeDetailsMainInfoForLargeScreenProps {
  enableBrilliantMaxSubscription?: boolean;
  home: IUnitDetailsDataHome;
  numBrilliantDevices: number;
  numIntegrations: number;
  openCancelBrilliantMaxModal: () => void;
  openEnrollBrilliantMaxModal: () => void;
  openRemoveHomeModal: () => void;
  subscription?: IHomeSubscription;
}

const renderSubscriptionButton = (
  status: ESubscriptionStatus,
  openCancelBrilliantMaxModal: () => void,
  openEnrollBrilliantMaxModal: () => void
): JSX.Element => {
  if (status === ESubscriptionStatus.NotSubscribed) {
    return (
      <button
        className="tertiary-btn-small"
        type="button"
        onClick={openEnrollBrilliantMaxModal}
      >
        Enroll in Brilliant Max
      </button>
    );
  }
  if (status === ESubscriptionStatus.SelfManaged) {
    return (
      <button
        className="tertiary-btn-small"
        disabled
        type="button"
        onClick={() => {}}
      >
        Enrolled in Brilliant Max
      </button>
    );
  }
  return (
    <button
      className="tertiary-btn-small tertiary-btn-small-alert"
      type="button"
      onClick={openCancelBrilliantMaxModal}
    >
      Cancel Brilliant Max
    </button>
  );
};

const HomeDetailsMainInfoForLargeScreen: React.FC<
  HomeDetailsMainInfoForLargeScreenProps
> = ({
  enableBrilliantMaxSubscription = false,
  home,
  numBrilliantDevices,
  numIntegrations,
  openCancelBrilliantMaxModal,
  openEnrollBrilliantMaxModal,
  openRemoveHomeModal,
  subscription,
}) => {
  const [dropDownMenuVisible, setDropDownMenuVisible] = useState(false);
  const [isFeatureEnabled] = useFeature("brilliant_max_subscription");
  const isBrilliantMaxEnabled =
    isFeatureEnabled || enableBrilliantMaxSubscription;
  const alertDetected = home.alerts.length !== 0;
  const alerts = getClimateAlertNames(home.alerts);
  const warningNames = getWarningNames(home.warnings);
  const hasWarnings = warningNames.length > 0;
  return (
    <div className="unit-main-container unit-main-container-pro-view">
      {renderSideLine({
        hasAlerts: alertDetected,
        hasWarnings,
        lineClassName: "unit-main-container-line",
      })}

      <div className="flex-display">
        <div className="unit-names">
          <Key title="Key" className="unit-names-img" />
          <div className="unit-names-info unit-names-info-pro-view">
            <div className="unit-names-info-name unit-title">
              {home.propertyName}
            </div>
            {renderTags({
              alerts,
              subscriptionStatus: isBrilliantMaxEnabled
                ? subscription?.status
                : undefined,
              warnings: warningNames,
            })}
          </div>
        </div>
        <div className="unit-details-container-right">
          <div className="unit-details-container unit-description-small unit-description-small-info">
            <div className="unit-details">
              {renderUnitDetailsElementAmount({
                nameOfSingleDevice: "Brilliant",
                numDevices: numBrilliantDevices,
              })}
            </div>
            <div className="unit-details">
              {renderUnitDetailsElementAmount({
                nameOfSingleDevice: "Integration",
                numDevices: numIntegrations,
              })}
            </div>
          </div>
          {isBrilliantMaxEnabled &&
            subscription &&
            renderSubscriptionButton(
              subscription.status,
              openCancelBrilliantMaxModal,
              openEnrollBrilliantMaxModal
            )}
          <div className="dashboard-settings-more">
            {dropDownMenuVisible && (
              <DropDownMenu>
                <button
                  className="dashboard-settings-more-btn unit-description-small"
                  type="button"
                  onClick={() => {
                    openRemoveHomeModal();
                    setDropDownMenuVisible(false);
                  }}
                >
                  Remove From ProView
                </button>
              </DropDownMenu>
            )}
            <More
              className="dashboard-settings-more-svg"
              onClick={() => setDropDownMenuVisible(!dropDownMenuVisible)}
              title="More Button"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default HomeDetailsMainInfoForLargeScreen;
